/**
 * /c/:slug/participate-leads - Participate Leads (CHAP-339). Team triage view
 * over the public omega-zeta.com "Commit now" intake: status filter chips,
 * per-status counts, and team/admin inline edit (status / owner / notes). Each
 * lead is a real worked item feeding the donor pipeline (CHAP-246), not an email.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useParticipateLeads, useUpdateParticipateLead } from '../hooks/useParticipateLeads.js';
import { useUserRoles } from '../hooks/useUserRoles.js';
import {
  pathLabel,
  intentLabel,
  intentIsTrustworthy,
  statusMeta,
  type ParticipateLeadStatus,
} from '../../shared/participate-leads.js';

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ParticipateLeads() {
  const { slug = '' } = useParams();
  const { data, isLoading, error } = useParticipateLeads(slug);
  const roles = useUserRoles();
  const canEdit = roles.allowedRoles.includes('team') || roles.allowedRoles.includes('admin');
  const myId = roles.user?.id ?? null;
  const update = useUpdateParticipateLead(slug);
  const [filter, setFilter] = useState<ParticipateLeadStatus | 'all'>('all');

  if (isLoading) return <div className="p-8 text-center text-sm text-neutral">Loading leads…</div>;
  if (error) return <div className="p-8 text-center text-sm text-red-700">Participate leads are available to chapter staff. {error.message}</div>;
  if (!data) return null;

  const records = filter === 'all' ? data.records : data.records.filter((r) => r.status === filter);
  const newCount = data.byStatus.new ?? 0;

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 flex items-start justify-between flex-wrap gap-3 min-w-0">
        <div className="min-w-0">
          <h2 className="text-navy text-xl sm:text-2xl font-bold">Participate Leads</h2>
          <p className="text-neutral text-sm mt-1">
            {data.total} {data.total === 1 ? 'lead' : 'leads'} from the omega-zeta.com “Commit now” form. Work each one; it feeds the donor pipeline.
          </p>
        </div>
        <div className="flex flex-col items-end flex-shrink-0">
          <span className="text-[10px] uppercase tracking-wide text-neutral font-bold">Needs attention</span>
          <span className="text-orange text-2xl font-bold">{newCount} new</span>
        </div>
      </header>

      {update.isError && (
        <div className="border-l-4 border-red-400 bg-red-50 text-red-800 text-sm p-3 rounded min-w-0">
          Could not save that change. Check your connection and try again{update.error?.message ? ` (${update.error.message})` : ''}.
        </div>
      )}

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2 min-w-0">
        <Chip active={filter === 'all'} onClick={() => setFilter('all')} label="All" count={data.total} />
        {data.statuses.map((s) => (
          <Chip
            key={s.key}
            active={filter === s.key}
            onClick={() => setFilter(s.key)}
            label={s.label}
            count={data.byStatus[s.key] ?? 0}
          />
        ))}
      </div>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 min-w-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-neutral">
            <tr className="border-b border-gray-100">
              <th className="pb-2 pr-3 font-medium">Lead</th>
              <th className="pb-2 pr-3 font-medium">Interest</th>
              <th className="pb-2 pr-3 font-medium">Status</th>
              <th className="pb-2 pr-3 font-medium">Owner</th>
              <th className="pb-2 pr-3 font-medium">Notes</th>
              <th className="pb-2 font-medium">Submitted</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => {
              const sm = statusMeta(r.status);
              return (
                <tr key={r.id} className="border-b border-gray-50 align-top">
                  <td className="py-2 pr-3 min-w-0">
                    <div className="font-medium text-navy">{r.name || '(no name)'}</div>
                    <a href={`mailto:${r.email}`} className="text-xs text-blue-700 hover:underline break-all">{r.email}</a>
                    {r.phone && <div className="text-xs text-neutral"><a href={`tel:${r.phone}`} className="hover:underline">{r.phone}</a></div>}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="text-xs">
                      <span className="font-medium text-navy">{pathLabel(r.path)}</span>
                      {/* CHAP-542: before the fix, every path except "unsure"
                          was stored as `commit` regardless of what the person
                          said. The raw value is kept, so the caveat travels
                          with it rather than the row being rewritten. */}
                      {intentIsTrustworthy(r.intent, r.createdAt) ? (
                        <span className="text-neutral"> · {intentLabel(r.intent)}</span>
                      ) : (
                        <span
                          className="text-neutral"
                          title="Recorded before the CHAP-542 fix of 2026-09-26, when every chosen path was stored as 'commit' regardless of what the person indicated. Read this as interest."
                        >
                          {' '}· <span className="line-through">{intentLabel(r.intent)}</span>{' '}
                          <span className="text-amber-800 font-medium">interest only</span>
                        </span>
                      )}
                    </div>
                    {r.amount && <div className="text-xs text-positive font-semibold">{r.amount}</div>}
                    {r.message && (
                      <div className="text-[11px] text-neutral mt-0.5 max-w-[16rem] truncate" title={r.message}>
                        “{r.message}”
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {canEdit ? (
                      <select
                        value={r.status}
                        onChange={(e) => update.mutate({ id: r.id, status: e.target.value as ParticipateLeadStatus })}
                        className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white"
                      >
                        {data.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    ) : (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${sm.cls}`}>{sm.label}</span>
                    )}
                    {r.workedAt && <div className="text-[10px] text-neutral mt-1">touched {fmtDate(r.workedAt)}</div>}
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {r.ownerName ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-navy">{r.ownerName}</span>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => update.mutate({ id: r.id, ownerUserId: null })}
                            className="text-neutral hover:text-red-600"
                            title="Unassign"
                          >×</button>
                        )}
                      </span>
                    ) : canEdit && myId ? (
                      <button
                        type="button"
                        onClick={() => update.mutate({ id: r.id, ownerUserId: myId })}
                        className="text-xs text-orange hover:underline font-medium"
                      >Claim</button>
                    ) : <span className="text-neutral">-</span>}
                  </td>
                  <td className="py-2 pr-3 min-w-[10rem]">
                    {canEdit ? (
                      <input
                        defaultValue={r.notes ?? ''}
                        placeholder="Add a note…"
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (r.notes ?? '').trim()) update.mutate({ id: r.id, notes: v || null });
                        }}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                      />
                    ) : (
                      <span className="text-xs text-neutral">{r.notes || '-'}</span>
                    )}
                  </td>
                  <td className="py-2 text-xs text-neutral whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                </tr>
              );
            })}
            {records.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-neutral text-xs">No leads {filter === 'all' ? 'yet' : `in “${statusMeta(filter).label}”`}.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Chip({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-lg border transition-colors min-w-0 ${active ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-200 hover:border-navy/40'}`}
    >
      <span className="font-semibold">{label}</span> <span className="opacity-70">{count}</span>
    </button>
  );
}
